import React, { useRef } from 'react';
import type { TaskItem } from '../../../types/topic';
// Note: TaskForm no longer depends on taskStore. Parent components should pass availableTags.
import { TaskFormEdit } from './TaskFormEdit';
import { TaskFormNew } from './TaskFormNew';

interface TaskFormProps {
  // Common props
  onCancel: () => void;

  // Props for edit mode
  task?: TaskItem;
  onSubmit?: (task: TaskItem) => void;
  onChange?: (task: TaskItem) => void;

  // Props for new task mode
  title?: string;
  description?: string;
  status?: 'todo' | 'doing' | 'done';
  tags?: string[];
  availableTags?: string[];
  deadline?: Date | null;
  assignedTo?: string | null;
  showDescription?: boolean;
  onTitleChange?: (value: string) => void;
  onDescriptionChange?: (value: string) => void;
  onTagsChange?: (tags: string[]) => void;
  onDeadlineChange?: (value: Date | null) => void;
  onAssigneeChange?: (userId: string | null) => void;
  onSubmitNewTask?: () => void;
  inputRef?: React.RefObject<HTMLTextAreaElement>;

  // Mode flag
  isEditMode?: boolean;

  // Hide buttons - useful for mobile when using MobileFullScreenModal
  hideButtons?: boolean;

  // Project ID for assignee picker
  projectId?: string;

  // Initial focus field
  focusField?: 'deadline' | 'title' | 'description' | 'tags';

  // Collaboration flag
  hasCollaborators?: boolean;
}

export const TaskForm: React.FC<TaskFormProps> = ({
  // Common props
  onCancel,

  // Edit mode props
  task,
  onSubmit,
  onChange,

  // New task props
  title = '',
  description = '',
  status = 'todo',
  tags = [],
  availableTags = [],
  deadline = null,
  assignedTo = null,
  onTitleChange,
  onDescriptionChange,
  onTagsChange,
  onDeadlineChange,
  onAssigneeChange,
  onSubmitNewTask,
  inputRef,

  // Mode flag
  isEditMode = false,

  // Optional project ID for assignee picker
  projectId,

  // Collaboration flag
  hasCollaborators = false,

  // Hide buttons option for mobile modals
  hideButtons = false,
}) => {
  // Use provided availableTags or empty array as fallback
  const availableTagsFromStore = availableTags || [];
  
  // Create a default ref if none provided
  const defaultInputRef = useRef<HTMLTextAreaElement>(null);
  const titleInputRef = inputRef || defaultInputRef;

  // Edit mode form
  if (isEditMode && task && onChange) {
    return (
      <TaskFormEdit
        task={task}
        projectId={projectId}
        availableTags={availableTagsFromStore}
        onSubmit={onSubmit || (() => {})}
        onChange={onChange}
        onCancel={onCancel}
        hasCollaborators={hasCollaborators}
        hideButtons={hideButtons}
      />
    );
  }

  // New task form
  if (!isEditMode && onTitleChange && onDescriptionChange && onTagsChange && 
      onDeadlineChange && onAssigneeChange && onSubmitNewTask) {
    return (
      <TaskFormNew
        title={title}
        description={description}
        tags={tags}
        deadline={deadline}
        assignedTo={assignedTo}
        status={status}
        projectId={projectId}
        availableTags={availableTagsFromStore}
        hasCollaborators={hasCollaborators}
        inputRef={titleInputRef}
        onTitleChange={onTitleChange}
        onDescriptionChange={onDescriptionChange}
        onTagsChange={onTagsChange}
        onDeadlineChange={onDeadlineChange}
        onAssigneeChange={onAssigneeChange}
        onSubmitNewTask={onSubmitNewTask}
        onCancel={onCancel}
      />
    );
  }

  // Fallback empty div if required props are missing
  return <div>Missing required props for TaskForm</div>;
};